import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Webcam from 'react-webcam';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _forEach from 'lodash/forEach';
import _map from 'lodash/map';
import _includes from 'lodash/includes';
import Fingerprint2 from 'fingerprintjs2';
import { v4 } from 'uuid';
import { webcamDomException, videoConstraints } from '../../constants/Webcam';
import {
  getMicConstraints,
  getWebcamConstraints,
  buildVideoConstraints,
  buildAudioConstraints,
  listAvailableMediaDevices,
} from '../../utils/mediaDeviceUtils';
import IMAGES from '../../constants/Images';
import {
  WEBCAM_PROCTORING_INTERVAL,
  VIDEO_PROCTORING_INTERVAL,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
  MIME_TYPE_WEBP,
  PROCTOR_IMAGE_UPLOAD_INITIAL_RETRY,
  PROCTOR_SIGNED_URL_INITIAL_BATCH_SIZE,
  PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_LOCALSTORAGE_DEFAULT_VALUE,
  PROCTOR_SIGNED_URL_RETRY,
  PROCTOR_MODE,
  PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_IMAGE_UPLOAD_STATUS,
  PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_IMAGE_PROCESS_STATUS,
  PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_TABID_SESSIONSTORAGE_KEY,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_DEFAULT_VALUE,
  IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_BATCH_API_STARTING_SEQUENCE,
  TAB_COMMUNICATION_CHANNEL,
  PROCTOR_HEARTBEAT_INTERVAL_SUCCESS,
  PROCTOR_HEARTBEAT_INTERVAL_FAILURE,
  WEBCAM_RECOVERY_FIBONACCI_BACKOFF_MS,
  MIME_TYPE_JPEG,
} from '../../constants/Proctor';
import {
  normalizeTimerSeconds,
  startWebcamErrorClock,
  stopWebcamErrorClockAndAccumulate,
  getWebcamErrorStartTimeMs,
  getWebcamErrorDurationSeconds,
  clearWebcamErrorStartTime,
  clearWebcamErrorStartTimeDuration,
} from '../../utils/webcamErrorTimer';
import { emitWebsocketEvent } from '../../actions/socket';
import { setAutoSubmitReason } from '../../actions/appState';
import { submitTest } from '../../actions/test';
import {
  AUTO_SUBMIT_REASONS,
  OFFLINE_DURATION_LOCALSTORAGE_KEY,
  OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
  OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY,
  SECURE_BROWSER_CONST,
} from '../../constants/Constants';

import {
  setWebcamException,
  uploadSnapshot,
  uploadFailedProctorImages,
  emitWebcamEvent,
  updateProctoringSignedUrls,
  updateSignedUrlsFromCache,
  setProctorNextImageSequence,
  setProctorSessionId,
  setProctorTabId,
  incrementWebcamRenderKey,
} from '../../actions/proctor';
import FullScreenProctorContainer from '../../containers/FullScreenProctorContainer';
import VisibilityProctorContainer from '../../containers/VisibilityProctorContainer';
import BrowserToleranceContainer from '../../containers/BrowserToleranceContainer';
import RouteChangeTracking from './RouteChangeTracking';
import {
  detectBrowser,
  emptyClipboard,
  getServerTime,
  getSystemDetails,
  getSecureBrowserMachineUUID,
  initiateSignedUrlLogging,
  isSafariBrowser,
  isFirefoxBrowser,
  isIOSWebView,
} from '../../utils/utils';
import { errorLogger, proctoringUBALogger } from '../../utils/trackingUtil';

import {
  WebcamDraggable,
  WebcamPreviewControllers,
  PreventCopyPastInput,
  BlinkingDot,
  WebcamNotDetectedIcon,
} from './Proctor.style';
import PreventCopyPast from './PreventCopyPast';
import { SOCKET_EVENTS } from '../../constants/Socket';
import WebcamNotDetectedModal from './WebcamNotDetectedModal';
import WebcamErrorBlockingModal from './WebcamErrorBlockingModal';
import RealTimeProctoringFeedback from '../RealTimeProctoringFeedback/RealTimeProctoringFeedback';
import VAD from '../shared/VAD/VAD';
import {
  getWebcamSnapshotSignedUrl,
  proctorHeartBeatLogger,
} from '../../api/proctor';
import { NETWORK_ERROR } from '../../constants/Http';
import {
  setProctoringImageStatusData,
  incrementProctorCacheBasedOnKey,
  setProctorTestStartTimeInCache,
  sleep,
} from '../../utils/helperFunction';
import { env } from '../../utils/request';
import MicrophoneNotDetectedModal from '../shared/MicrophoneNotDetectedModal';
import exitSecureBrowserApplication from '../SecureBrowser/ExitAssessment/exitAssessment.utils';
import { requestAudioVideoPermission } from '../../utils/permissions';
import useDraggable from '../../hooks/useDraggable';

/**
 * This component primarally controls webcam proctoring and acts as
 * the container for FullScreenProctoring, VisibilityProctoring, and
 * Route change proctoring
 * @param {*} props
 */

// This variable is used to differentiate between original and duplicate tab ( important )
window.currentEmitTime = new Date();

function Proctor(props) {
  const {
    location,
    test,
    testSolutionSet,
    enableWebcam,
    webcamEnabled,
    allowWebcamPermission,
    allowAudioPermission,
    denyWebcamPermission,
    audioPermissionLoading,
    webcamPermissionLoading,
    denyAudioPermission,
    setFingerPrint,
    socketActive,
    fingerprint,
    auth,
    trackChangeRoute,
  } = props;
  const webcamRef = useRef(null);
  const webcamDraggableRef = useRef(null);
  const batchApiFirstTrigger = useRef(false);

  const proctorBatchSignedUrlsRef = useRef({});
  const isMountedRef = useRef(true);
  const webcamRecoveryRef = useRef({
    inProgress: false,
    attempt: 0,
    reason: null,
    lastErrorName: null,
    startedAt: null,
  });
  const webcamRecoveryAttemptResolveRef = useRef(null);
  const [showWebcamPreview, setShowWebcamPreview] = useState(true);
  const [isWebcamCapturingStarted, setWebcamCapturing] = useState(false);
  const [
    realtimeFeedbackModalSignal,
    setRealtimeFeedbackModalSignal,
  ] = useState(0);
  const webcamErrorConsecutiveRef = useRef({ count: 0 });
  const webcamErrorForceSubmitRef = useRef(false);
  const strictCameraEnforcementActivatedAtRef = useRef(null);
  const [isSystemOnline, setIsSystemOnline] = useState(true);
  const [isCacheLoopRunning, setIsCacheLoopRunning] = useState(false);
  const [showWebcamNotDetectedModal, setShowWebcamNotDetectedModal] = useState(
    false
  );
  const [
    webcamErrorRemainingSeconds,
    setWebcamErrorRemainingSeconds,
  ] = useState(null);
  const [
    showMicrophoneNotDetectedModal,
    setShowMicrophoneNotDetectedModal,
  ] = useState(false);
  const [microphoneMuteData, setMicrophoneMuteData] = useState(null);
  const solutionId = _get(testSolutionSet, 'id');
  const token = _get(testSolutionSet, 'token');
  const preventCopyRef = useRef(null);
  const proctorCaptureContextRef = useRef({
    isWebcamCapturingStarted: false,
    isExtendTestInitialPhase: false,
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const failedProctorImages = localStorage.getItem(
    FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY
  );

  const {
    assessmentId,
    webcamPermission,
    isOffline,
    proctoringSignedUrls,
    fetchingSignedUrls,
    isProctoringABEnabled,
    signedUrlBatchSize,
    isRealtimeFeedbackEnabled,
    proctorSessionId,
    micPermissions,
    nextProctorImageSequence,
    tabId,
    disableMultiTab,
    isSecureBrowserEnable,
    isProctorBatchABEnabled,
    isAiInterview,
    offsetTime,
    deviceRefreshCount,
    webcamRenderKey,
  } = useSelector(
    state => ({
      assessmentId: state?.assessment?.assessmentId,
      webcamPermission: state?.proctor?.webcamPermission,
      isOffline: state?.appStatus?.isOffline,
      proctoringSignedUrls: state?.proctor?.proctoringSignedUrls,
      fetchingSignedUrls: state?.proctor?.fetchingSignedUrls,
      isProctoringABEnabled: state?.proctor?.isProctoringABEnabled,
      isProctorBatchABEnabled: state?.proctor?.isProctorBatchABEnabled,
      signedUrlBatchSize: state?.proctor?.signedUrlBatchSize,
      isRealtimeFeedbackEnabled: state?.proctor?.isRealtimeFeedbackEnabled,
      proctorSessionId: state?.proctor?.proctorSessionId,
      micPermissions: state?.test?.settings?.user_permissions?.mic,
      nextProctorImageSequence: state?.proctor?.nextProctorImageSequence,
      tabId: state?.proctor?.proctorTabId,
      disableMultiTab:
        state?.test?.system_settings?.proctoring?.disableMultiTab,
      isSecureBrowserEnable: state?.test?.isSecureBrowserEnable,
      isAiInterview: state?.test?.isAiInterview,
      offsetTime: state?.appStatus?.offsetTime,
      deviceRefreshCount: state?.appStatus?.deviceRefreshCount || 0,
      webcamRenderKey: state.proctor.webcamRenderKey,
    }),
    shallowEqual
  );

  // When the device-only modal closes after a webcam pick, remount react-webcam
  // so it re-acquires using the now-current stored deviceId.
  useEffect(() => {
    if (deviceRefreshCount > 0) {
      dispatch(incrementWebcamRenderKey());
    }
  }, [deviceRefreshCount]);
  const { position, dragHandlers } = useDraggable({
    draggableRef: webcamDraggableRef,
    initialPosition: {
      x: window.innerWidth - 250,
      y: 80,
    },
  });

  const [heartBeatIntervalTime, setHeartBeatIntervalTime] = useState(
    PROCTOR_HEARTBEAT_INTERVAL_SUCCESS
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      webcamRecoveryAttemptResolveRef.current = null;
    };
  }, []);

  const logSecureBrowser = useCallback(
    payload => {
      if (isSecureBrowserEnable && window.electron) {
        const { sendMsgToElectron } = window.electron;
        sendMsgToElectron(SECURE_BROWSER_CONST.EVENTS_TO_ELECTRON.SEND_LOGS, {
          ...payload,
        });
      }
    },
    [isSecureBrowserEnable]
  );

  const triggerRealtimeFeedbackModal = useCallback(
    reason => {
      proctoringUBALogger(
        'warning:realtime-feedback',
        `Trigger realtime feedback modal due to webcam issue: ${reason || 'NA'}`
      );
      logSecureBrowser({
        message: 'Trigger realtime feedback modal due to webcam issue',
        reason: reason || 'NA',
      });
      setRealtimeFeedbackModalSignal(s => s + 1);
    },
    [logSecureBrowser, proctoringUBALogger]
  );

  const {
    isBrowserToleranceEnabled,
    proctorEnabled,
    videoProctoringEnabled,
    imageProctoringEnabled,
    audioProctoringEnabled,
  } = useMemo(() => {
    const isProctorEnabled = _get(test, 'settings.proctor.enabled');
    const isImageProctoringEnabled = _get(test, 'settings.proctor.snapshot');
    const isVideoProctoringEnabled = _get(
      test,
      'settings.proctor.video.enabled'
    );
    const tempBrowserTolerance = _get(
      test,
      'settings.proctor.browser_tolerance.enabled'
    );
    const isAudioProctoringEnabled =
      test?.settings?.proctor?.audio?.enabled &&
      (isImageProctoringEnabled || isVideoProctoringEnabled) &&
      isProctorEnabled;

    return {
      proctorEnabled: isProctorEnabled,
      imageProctoringEnabled: isImageProctoringEnabled,
      videoProctoringEnabled: isVideoProctoringEnabled,
      isBrowserToleranceEnabled:
        tempBrowserTolerance &&
        isProctorEnabled &&
        test.session_state === 'ACTIVE',
      audioProctoringEnabled: isAudioProctoringEnabled,
    };
  }, [test]);

  const logAvailableMediaDevices = useCallback(
    async reason => {
      const devices = await listAvailableMediaDevices();
      proctoringUBALogger(
        'info:media-devices',
        JSON.stringify({ reason: reason || 'NA', devices })
      );
      logSecureBrowser({
        message: 'available media devices',
        reason: reason || 'NA',
        devices,
      });
    },
    [logSecureBrowser, proctoringUBALogger]
  );

  // Log the mic/webcam device list (with labels) once up front, and again
  // every time the OS device list changes, so we can tell after the fact
  // whether e.g. an external webcam was plugged in/dropped mid-test.
  useEffect(() => {
    if (!proctorEnabled) return undefined;
    logAvailableMediaDevices('initial');
    const handleDeviceChange = () => logAvailableMediaDevices('devicechange');
    navigator.mediaDevices?.addEventListener?.(
      'devicechange',
      handleDeviceChange
    );
    return () => {
      navigator.mediaDevices?.removeEventListener?.(
        'devicechange',
        handleDeviceChange
      );
    };
  }, [proctorEnabled, logAvailableMediaDevices]);

  const webcamErrorAutoSubmitTimerSeconds = useMemo(() => {
    const fromSettings = normalizeTimerSeconds(
      test?.settings?.proctor?.strict_camera?.inactive_timeout
    );
    if (fromSettings !== null) return fromSettings;
    // TEMP (mock): default to 30s in non-production so we can test the feature end-to-end.
    // if (process?.env?.NODE_ENV !== 'production') return 30;
    return null;
  }, [test]);

  // New auto-submit flow should only run for advanced proctoring (1s cadence).
  // For older/basic flows (e.g. image snapshot at 15s), keep existing behaviour unchanged.
  const isWebcamErrorAutoSubmitEnabled =
    webcamErrorAutoSubmitTimerSeconds !== null &&
    webcamErrorAutoSubmitTimerSeconds > 0 &&
    test?.settings?.proctor?.strict_camera?.enabled &&
    videoProctoringEnabled;

  // Strict camera enforcement should only begin once the test session is ACTIVE
  // (i.e. after candidate clicks "Start Assessment").
  const isExtendTestInitialPhase = useMemo(() => {
    const isExtendTestInInitialPhase =
      testSolutionSet?.add_test_duration &&
      testSolutionSet?.add_test_duration > 0 &&
      !testSolutionSet?.has_resumed_test;

    return isExtendTestInInitialPhase;
  }, [testSolutionSet]);

  const isStrictCameraEnforcementActive =
    isWebcamErrorAutoSubmitEnabled &&
    !test?.is_expired &&
    !test?.isTestSubmitted;

  const getNowMs = useCallback(() => getServerTime().valueOf(), []);

  const startWebcamErrorClockIfEnabled = useCallback(() => {
    if (!isWebcamErrorAutoSubmitEnabled) return;
    if (!token || isExtendTestInitialPhase || !isWebcamCapturingStarted) return;
    // Once we’ve initiated a force-submit due to webcam error, do not start/emit again.
    if (webcamErrorForceSubmitRef.current) return;
    if (test?.is_expired || test?.isTestSubmitted) return;
    const { startedNew, startMs } = startWebcamErrorClock(getNowMs());
    if (startedNew && startMs) {
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.WEBCAM_ERROR_START,
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
          eventData: {
            webcamErrorStartTime: String(startMs),
          },
        })
      );
    }
  }, [
    dispatch,
    getNowMs,
    isWebcamErrorAutoSubmitEnabled,
    test?.isTestSubmitted,
    test?.is_expired,
    token,
    isExtendTestInitialPhase,
    isWebcamCapturingStarted,
  ]);

  const stopWebcamErrorClockIfEnabled = useCallback(() => {
    if (!isWebcamErrorAutoSubmitEnabled) return;
    const result = stopWebcamErrorClockAndAccumulate(getNowMs());
    if (
      result?.nextDurationSec !== null &&
      result?.nextDurationSec !== undefined
    ) {
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.WEBCAM_ERROR_DURATION,
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
          eventData: {
            webcamErrorDuration: String(result.nextDurationSec),
          },
        })
      );
    }
  }, [dispatch, getNowMs, isWebcamErrorAutoSubmitEnabled]);

  const clearWebcamErrorBlockingUi = useCallback(() => {
    setWebcamErrorRemainingSeconds(null);
    setShowWebcamNotDetectedModal(false);
  }, []);

  // Single entry-point: show blocking modal + start incident clock (only when enforcement is active)
  const showWebcamErrorBlockingModalIfEligible = useCallback(() => {
    if (!isStrictCameraEnforcementActive) return;
    if (!token || isExtendTestInitialPhase || !isWebcamCapturingStarted) return;
    // Once we’ve initiated a force-submit due to webcam error, do not show/start again.
    if (webcamErrorForceSubmitRef.current) return;
    setShowWebcamNotDetectedModal(true);
    startWebcamErrorClockIfEnabled();
  }, [
    isStrictCameraEnforcementActive,
    startWebcamErrorClockIfEnabled,
    token,
    isExtendTestInitialPhase,
    isWebcamCapturingStarted,
  ]);

  const proctoringMode = useMemo(() => {
    const mode =
      imageProctoringEnabled || videoProctoringEnabled
        ? videoProctoringEnabled
          ? PROCTOR_MODE.VIDEO
          : PROCTOR_MODE.IMAGE
        : '';
    return mode;
  }, [videoProctoringEnabled, imageProctoringEnabled]);

  const isEnableWebcam =
    proctorEnabled &&
    (imageProctoringEnabled || videoProctoringEnabled) &&
    !!solutionId;
  const enableRouteChangeProctor =
    proctorEnabled && !!solutionId && !test.is_expired;

  const scheduleWebcamRecovery = useCallback(
    async ({ reason, error } = {}) => {
      if (webcamRecoveryRef.current.inProgress) return;
      if (!isMountedRef.current) return;

      webcamRecoveryRef.current = {
        inProgress: true,
        attempt: 0,
        reason: reason || 'unknown',
        lastErrorName: error?.name || error || null,
        startedAt: Date.now(),
      };

      proctoringUBALogger(
        'warning:webcam-recovery',
        `webcam recovery started (reason=${
          webcamRecoveryRef.current.reason
        }, lastError=${webcamRecoveryRef.current.lastErrorName || 'NA'})`
      );
      logSecureBrowser({
        message: 'webcam recovery started',
        reason: webcamRecoveryRef.current.reason,
        lastError: webcamRecoveryRef.current.lastErrorName || 'NA',
      });

      for (let i = 0; i < WEBCAM_RECOVERY_FIBONACCI_BACKOFF_MS.length; i += 1) {
        if (!isMountedRef.current) return;
        if (!webcamRecoveryRef.current.inProgress) return;

        const delayMs = WEBCAM_RECOVERY_FIBONACCI_BACKOFF_MS[i];
        const attempt = i + 1;
        webcamRecoveryRef.current.attempt = attempt;

        proctoringUBALogger(
          'warning:webcam-recovery',
          `webcam recovery attempt ${attempt}/${WEBCAM_RECOVERY_FIBONACCI_BACKOFF_MS.length} scheduled in ${delayMs}ms (reason=${webcamRecoveryRef.current.reason})`
        );
        logSecureBrowser({
          message: 'webcam recovery attempt scheduled',
          attempt,
          delayMs,
          reason: webcamRecoveryRef.current.reason,
        });

        // Fibonacci backoff wait
        // eslint-disable-next-line no-await-in-loop
        await sleep(delayMs);
        if (!isMountedRef.current) return;
        if (!webcamRecoveryRef.current.inProgress) return;

        // Try to "touch" the media devices so the browser re-validates device availability.
        // Keep audio flag consistent with the rest of proctoring so audio+video setups recover together.
        try {
          // eslint-disable-next-line no-await-in-loop
          const tmpStream = await navigator.mediaDevices.getUserMedia({
            ...getWebcamConstraints(videoConstraints),
            ...(micPermissions || audioProctoringEnabled
              ? { audio: buildAudioConstraints() }
              : {}),
          });
          tmpStream?.getTracks?.().forEach(t => t.stop());
        } catch (e) {
          webcamRecoveryRef.current.lastErrorName = e?.name || e;
          proctoringUBALogger(
            'warning:webcam-recovery',
            `webcam recovery attempt ${attempt} getUserMedia(audio+video) failed: ${e?.name ||
              e}`
          );
          logSecureBrowser({
            message: 'webcam recovery attempt getUserMedia(audio+video) failed',
            attempt,
            reason: webcamRecoveryRef.current.reason,
            errorName: e?.name || e,
          });
        }

        // Force remount react-webcam so it re-acquires a fresh stream.
        dispatch(incrementWebcamRenderKey());

        // Wait for react-webcam to call onUserMedia (handleUserMedia) for this attempt.
        // eslint-disable-next-line no-await-in-loop
        const recovered = await Promise.race([
          new Promise(resolve => {
            webcamRecoveryAttemptResolveRef.current = resolve;
          }),
          sleep(1200).then(() => false),
        ]);
        webcamRecoveryAttemptResolveRef.current = null;

        if (recovered) {
          // handleUserMedia() logs success + clears flags
          return;
        }
      }

      // Exhausted all retries
      const exhaustedMeta = {
        reason: webcamRecoveryRef.current.reason,
        attempts: WEBCAM_RECOVERY_FIBONACCI_BACKOFF_MS.length,
        lastError: webcamRecoveryRef.current.lastErrorName || 'NA',
      };
      proctoringUBALogger(
        'error:webcam-recovery',
        `webcam recovery exhausted (attempts=${exhaustedMeta.attempts}, reason=${exhaustedMeta.reason}, lastError=${exhaustedMeta.lastError})`
      );
      logSecureBrowser({
        message: 'webcam recovery exhausted',
        ...exhaustedMeta,
      });

      webcamRecoveryRef.current.inProgress = false;
      // Do NOT call denyWebcamPermission here; we only surface the issue.
      dispatch(
        setWebcamException(
          webcamRecoveryRef.current.lastErrorName ||
            webcamDomException.NotReadableError
        )
      );
    },
    [
      dispatch,
      logSecureBrowser,
      proctoringUBALogger,
      micPermissions,
      audioProctoringEnabled,
    ]
  );

  const handleMinimizeWebcam = useCallback(() => {
    setShowWebcamPreview(false);
    webcamRef.current.video.hidden = true;
    webcamRef.current.video.height = 35;
    webcamDraggableRef.current.style.height = '35px';
  }, [webcamRef.current]);

  const handleMaximizeWebcam = useCallback(() => {
    setShowWebcamPreview(true);
    webcamRef.current.video.hidden = false;
    webcamRef.current.video.height = 150;
    webcamDraggableRef.current.style.height = '150px';
  }, [webcamRef.current]);

  /* eslint-disable-next-line consistent-return */
  const webcamSnapShotCaptureInterval = useMemo(() => {
    if (proctorEnabled && imageProctoringEnabled) {
      return WEBCAM_PROCTORING_INTERVAL;
      /* eslint-disable-next-line no-else-return */
    } else if (proctorEnabled && videoProctoringEnabled) {
      return VIDEO_PROCTORING_INTERVAL;
    }
    return WEBCAM_PROCTORING_INTERVAL;
  }, [proctorEnabled, imageProctoringEnabled, videoProctoringEnabled]);

  const hasModernizr = window.Modernizr;

  useEffect(() => {
    proctorCaptureContextRef.current = {
      isWebcamCapturingStarted,
      isExtendTestInitialPhase,
    };
  }, [isWebcamCapturingStarted, isExtendTestInitialPhase]);

  const getSignedUrlData = useCallback(
    ({
      authToken,
      assessmentID,
      nextImageSequence,
      signedUrlPostFetchcallback,
      cachedData = false,
    }) => {
      // The use case rises when user hard refresh and we get the cache
      // data from session storage but token call is still in progress
      if (cachedData && !authToken) {
        signedUrlPostFetchcallback({ url: '', headers: {} });
      } else {
        const requestTransactionId = v4();
        getWebcamSnapshotSignedUrl({
          token: authToken,
          assessmentId: assessmentID,
          imageSequence: nextImageSequence,
          requestTransactionId,
          mode: proctoringMode,
        })
          .then(response => {
            if (!cachedData && nextImageSequence === 1)
              dispatch(
                setProctorNextImageSequence(
                  +response?.data?.image_sequence || 1
                )
              );
            signedUrlPostFetchcallback({
              url: response?.data?.signed_url,
              headers: response?.data?.headers,
            });
          })
          .catch(error => {
            errorLogger(error);
            proctoringUBALogger(
              'error:singed-url',
              `error-status: ${error?.response?.status ||
                error?.name}, request-transaction-id: ${requestTransactionId}`
            );
            setProctoringImageStatusData({
              imageSequence: nextImageSequence,
              status: PROCTOR_IMAGE_UPLOAD_STATUS.fail,
              retry: cachedData?.retry || 0,
              reason: PROCTOR_IMAGE_PROCESS_STATUS.fetch,
            });
            incrementProctorCacheBasedOnKey(
              PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY
            );
            signedUrlPostFetchcallback({ url: '', headers: {} });
          });
        if (!cachedData)
          dispatch(setProctorNextImageSequence(+nextImageSequence + 1));
      }
    },
    [proctoringMode]
  );

  const cacheUploadImageDelayLoop = (fn, delay, failedProctorUploadImage) => {
    let delayLoopTimeout;
    let lastCacheUploadImageCallback = false;
    return (imageDetails, index) => {
      delayLoopTimeout = setTimeout(() => {
        if (index === Object.keys(failedProctorUploadImage).length - 1) {
          lastCacheUploadImageCallback = () => {
            setIsCacheLoopRunning(false);
            clearTimeout(delayLoopTimeout);
          };
        }
        fn(
          imageDetails,
          failedProctorUploadImage,
          lastCacheUploadImageCallback
        );
      }, (index + 1) * delay + 200);
    };
  };

  const fetchSignedUrlForCacheImages = (
    { compressedBase64, contentType, signedUrl, eventGuid, retry },
    failedProctorUploadImage,
    lastCacheUploadImageCallback
  ) => {
    const PROCTOR_FAILED_IMAGES_VALUES =
      JSON.parse(
        localStorage.getItem(FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY)
      ) || FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_DEFAULT_VALUE;
    if (PROCTOR_FAILED_IMAGES_VALUES?.[eventGuid]) {
      delete PROCTOR_FAILED_IMAGES_VALUES[eventGuid];
      localStorage.setItem(
        FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
        JSON.stringify(PROCTOR_FAILED_IMAGES_VALUES)
      );
    }
    if (!signedUrl) {
      getSignedUrlData({
        cachedData: failedProctorUploadImage[eventGuid],
        authToken: token,
        assessmentID: assessmentId,
        nextImageSequence: eventGuid,
        signedUrlPostFetchcallback: signedurl => {
          dispatch(
            uploadFailedProctorImages({
              compressedBase64,
              contentType,
              signedUrl: signedurl,
              eventGuid,
              sequenceRetry: retry,
              lastCacheUploadImageCallback,
              isRetryingLocalStorageImage: true,
            })
          );
        },
      });
    } else {
      dispatch(
        uploadFailedProctorImages({
          compressedBase64,
          contentType,
          signedUrl,
          eventGuid,
          sequenceRetry: retry,
          lastCacheUploadImageCallback,
          isRetryingLocalStorageImage: true,
        })
      );
    }
  };

  useEffect(() => {
    dispatch(setProctorSessionId(v4()));
    let cachedSignedUrls = localStorage.getItem(
      PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY
    );
    if (cachedSignedUrls) {
      cachedSignedUrls = JSON.parse(cachedSignedUrls);
      const proctoringSignedUrlsLength = Object.keys(cachedSignedUrls)?.length;
      if (proctoringSignedUrlsLength > 0) {
        dispatch(updateSignedUrlsFromCache(cachedSignedUrls));
      }
    } else {
      localStorage.setItem(
        PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY,
        JSON.stringify(PROCTOR_SIGNED_URL_LOCALSTORAGE_DEFAULT_VALUE)
      );
    }

    const cachedProctorNextImageSequence = sessionStorage.getItem(
      PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
    );
    if (cachedProctorNextImageSequence)
      dispatch(setProctorNextImageSequence(cachedProctorNextImageSequence));
    let proctorTabId = sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY);
    if (!proctorTabId) {
      proctorTabId = v4();

      sessionStorage.setItem(PROCTOR_TABID_SESSIONSTORAGE_KEY, proctorTabId);
    }
    dispatch(setProctorTabId(proctorTabId));
  }, []);

  useEffect(() => {
    const failedProctorUploadImages = failedProctorImages
      ? JSON.parse(failedProctorImages)
      : {};
    if (
      (!isOffline ||
        isSystemOnline ||
        Object.keys(failedProctorUploadImages)?.length >= 1) &&
      !isCacheLoopRunning &&
      solutionId &&
      (videoProctoringEnabled || imageProctoringEnabled)
    ) {
      const failedProctorUploadImageTemp = { ...failedProctorUploadImages };
      Object.values(failedProctorUploadImageTemp).forEach(
        cacheUploadImageDelayLoop(
          fetchSignedUrlForCacheImages,
          1000,
          failedProctorUploadImageTemp
        )
      );
      if (Object.keys(failedProctorUploadImages)?.length >= 1)
        setIsCacheLoopRunning(true);
    }
  }, [
    isSystemOnline,
    failedProctorImages,
    isOffline,
    isCacheLoopRunning,
    solutionId,
    videoProctoringEnabled,
    imageProctoringEnabled,
  ]);

  /**
   * Download Modernizr library only if proctor is enabled
   */
  useEffect(() => {
    if (!hasModernizr && proctorEnabled) {
      if (!window.Modernizr) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/modernizr/2.8.3/modernizr.min.js';
        const headScript = document.getElementsByTagName('script')[0];
        headScript.parentNode.insertBefore(script, headScript);
      }
    }
  }, [hasModernizr, proctorEnabled]);

  // enable webcam
  useEffect(() => {
    if (!webcamEnabled && isEnableWebcam && !test.is_expired) enableWebcam();
  }, [enableWebcam, isEnableWebcam, webcamEnabled, test.is_expired]);

  useEffect(() => {
    const proctoringSignedUrlsLength = Object.keys(proctoringSignedUrls)
      ?.length;

    const cachedProctorNextImageSequence = sessionStorage.getItem(
      PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
    );

    let cachedBactedUrlNextImageSequence = localStorage.getItem(
      PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY
    );
    const isExtendedTest =
      testSolutionSet?.add_test_duration &&
      testSolutionSet?.add_test_duration > 0;
    const toTriggerBatchApi =
      nextProctorImageSequence >= 3 &&
      (nextProctorImageSequence <= PROCTOR_SIGNED_URL_INITIAL_BATCH_SIZE ||
        isExtendedTest) &&
      !batchApiFirstTrigger.current;

    let reTriggerBatchApi = false;
    if (cachedBactedUrlNextImageSequence)
      reTriggerBatchApi =
        nextProctorImageSequence >
        cachedBactedUrlNextImageSequence - signedUrlBatchSize / 4;

    // The batch url is being called on different percentages
    if (
      solutionId &&
      (videoProctoringEnabled || imageProctoringEnabled) &&
      signedUrlBatchSize &&
      (toTriggerBatchApi || reTriggerBatchApi) &&
      !fetchingSignedUrls &&
      !test.is_expired &&
      !test.isTestSubmitted &&
      !isOffline &&
      isProctoringABEnabled &&
      signedUrlBatchSize
    ) {
      if (!cachedBactedUrlNextImageSequence) {
        localStorage.setItem(
          PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
          cachedProctorNextImageSequence
        );
        cachedBactedUrlNextImageSequence = cachedProctorNextImageSequence;
      }
      dispatch(
        updateProctoringSignedUrls(
          {
            token,
            assessmentId: solutionId,
            mode: proctoringMode,
            batchSize: signedUrlBatchSize,
            lastSignedUrl:
              proctoringSignedUrls?.[cachedBactedUrlNextImageSequence - 1],
            nextImageSequence:
              (+cachedBactedUrlNextImageSequence <
                cachedProctorNextImageSequence &&
              proctoringSignedUrlsLength !== 0
                ? cachedProctorNextImageSequence
                : cachedBactedUrlNextImageSequence) || 1,
          },
          PROCTOR_SIGNED_URL_RETRY
        )
      );
      batchApiFirstTrigger.current = true;
    }
  }, [
    token,
    proctoringSignedUrls,
    solutionId,
    fetchingSignedUrls,
    isProctoringABEnabled,
    test.is_expired,
    signedUrlBatchSize,
    isOffline,
    imageProctoringEnabled,
    videoProctoringEnabled,
    proctoringMode,
    nextProctorImageSequence,
    batchApiFirstTrigger.current,
    testSolutionSet?.add_test_duration,
  ]);

  // disable webcam and webcam light after test is over
  useEffect(() => {
    if (test.is_expired && webcamRef.current && webcamRef.current.stream) {
      webcamRef.current.video.pause();
      webcamRef.current.video.src = '';
      const tracks = webcamRef.current.stream.getTracks();
      if (_isArray(tracks)) {
        _forEach(tracks, track => {
          track.stop();
        });
      }
    }
  }, [test.is_expired]);

  const submitWebcamSnapshot = useCallback(
    ({ webcamSnapshotDataURI, eventGuid, mimeType, nextImageSequence }) => {
      const signedUrls = proctorBatchSignedUrlsRef?.current || {};
      if (
        isProctoringABEnabled &&
        (nextImageSequence >= PROCTOR_BATCH_API_STARTING_SEQUENCE ||
          (signedUrls[nextImageSequence] && isProctorBatchABEnabled))
      ) {
        // let signedUrls = localStorage.getItem(
        //   PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY
        // );
        // signedUrls = signedUrls ? JSON.parse(signedUrls) : signedUrls;
        const signedUrl = signedUrls ? signedUrls[nextImageSequence] : false;
        if (signedUrl) {
          dispatch(
            uploadSnapshot(
              signedUrl,
              webcamSnapshotDataURI,
              mimeType,
              eventGuid,
              PROCTOR_IMAGE_UPLOAD_INITIAL_RETRY,
              isProctoringABEnabled
            )
          );
        } else {
          // signed url error
          incrementProctorCacheBasedOnKey(
            PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY
          );
        }
        dispatch(setProctorNextImageSequence(+nextImageSequence + 1));
      } else {
        getSignedUrlData({
          authToken: token,
          assessmentID: assessmentId,
          nextImageSequence,
          signedUrlPostFetchcallback: signedurl => {
            dispatch(
              uploadSnapshot(
                signedurl,
                webcamSnapshotDataURI,
                mimeType,
                eventGuid,
                PROCTOR_IMAGE_UPLOAD_INITIAL_RETRY
              )
            );
          },
        });
      }
    },
    [
      dispatch,
      uploadSnapshot,
      token,
      assessmentId,
      isProctoringABEnabled,
      isProctorBatchABEnabled,
    ]
  );

  const proctoringWebcamErrorCallback = async () => {
    let cachedProctorNextImageSequence = sessionStorage.getItem(
      PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
    );
    cachedProctorNextImageSequence = cachedProctorNextImageSequence || 1;
    const nextImageSequenceFromSetting = localStorage.getItem(
      IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY
    );
    if (nextImageSequenceFromSetting && cachedProctorNextImageSequence < 2) {
      cachedProctorNextImageSequence = nextImageSequenceFromSetting;
    }
    if (cachedProctorNextImageSequence === 1) {
      clearWebcamErrorStartTimeDuration();
    }
    incrementProctorCacheBasedOnKey(
      PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY
    );
    if (!isWebcamCapturingStarted) {
      setWebcamCapturing(true);
    }
    setProctoringImageStatusData({
      imageSequence: cachedProctorNextImageSequence,
      status: PROCTOR_IMAGE_UPLOAD_STATUS.webcamError,
      reason: PROCTOR_IMAGE_PROCESS_STATUS.webcamError,
    });
    dispatch(setProctorNextImageSequence(+cachedProctorNextImageSequence + 1));
    const stream = webcamRef.current?.stream;
    const videoTrack = stream?.getVideoTracks?.();
    const videoElement = webcamRef.current?.video;

    const cameraPermission = await navigator.permissions
      ?.query?.({ name: 'camera' })
      .then(res => res.state)
      .catch(() => 'unsupported');

    // Check if webcam is actually unavailable: stream inactive OR no tracks OR tracks ended/muted OR permission denied
    const hasActiveVideoTrack =
      videoTrack?.length > 0 &&
      videoTrack.some(track => track.readyState === 'live' && track.enabled);
    const isWebcamUnavailable =
      !stream?.active || !hasActiveVideoTrack || cameraPermission === 'denied';

    // NOTE: calling getUserMedia() alone doesn't fix react-webcam if it holds an inactive stream.
    // We trigger the realtime feedback modal and start fibonacci recovery ONLY after 2 consecutive errors.
    if (isWebcamUnavailable) {
      showWebcamErrorBlockingModalIfEligible();
      const prevCount = webcamErrorConsecutiveRef.current.count || 0;
      const nextCount = Math.min(prevCount + 1, 2);
      webcamErrorConsecutiveRef.current.count = nextCount;

      // Fire only on transition 1 -> 2, then keep latched until reset by success.
      if (prevCount === 1 && nextCount === 2) {
        triggerRealtimeFeedbackModal(
          'snapshot-capture-stream-inactive:consecutive-2'
        );
        scheduleWebcamRecovery({
          reason: 'snapshot-capture-stream-inactive',
        });
      }
    }

    if (isSecureBrowserEnable && window.electron) {
      const { sendMsgToElectron } = window.electron;
      sendMsgToElectron(SECURE_BROWSER_CONST.EVENTS_TO_ELECTRON.SEND_LOGS, {
        webcamStreamActive: stream?.active,
        videoTracks: videoTrack?.length,
        videoTrackStatus:
          videoTrack?.length > 0 ? videoTrack[0]?.readyState : 'NA',
        videoElementPresent: !!videoElement,
        cameraPermission,
        muted: videoTrack?.length > 0 ? videoTrack[0]?.enabled : 'NA',
      });
    }

    proctoringUBALogger(
      'info:image-not-initiated',
      `webcam stream active: ${stream?.active},  webcam muted: ${
        videoTrack?.length > 0 ? videoTrack[0]?.enabled : 'NA'
      }, video tracks: ${videoTrack?.length}, video track state: ${
        videoTrack?.length > 0 ? videoTrack[0]?.readyState : 'NA'
      }, video element present: ${!!videoElement}, video element readyState: ${
        videoElement?.readyState
      }, camera permission: ${cameraPermission}, AB Testing Enabled: ${isProctoringABEnabled}, timestamp: ${new Date().toISOString()}, userAgent: ${
        navigator.userAgent
      }`
    );
  };

  const imageCapturingProctoringCallback = () => {
    initiateSignedUrlLogging('imageCapturingProctoringCallback');
    let cachedProctorNextImageSequence = sessionStorage.getItem(
      PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
    );
    cachedProctorNextImageSequence = cachedProctorNextImageSequence || 1;
    if (cachedProctorNextImageSequence === 1) {
      clearWebcamErrorStartTimeDuration();
    }
    const webcamSnapshotDataURI = webcamRef.current.getScreenshot({
      width: 640,
      height: 480,
    });
    // If we fail to capture, treat it as webcam unavailable (do NOT stop the timer).
    if (!webcamSnapshotDataURI) {
      proctoringWebcamErrorCallback();
      return;
    }
    // Capture succeeded: if webcam had errored previously, stop the clock, emit duration event and close the modal.
    if (isWebcamErrorAutoSubmitEnabled) {
      stopWebcamErrorClockIfEnabled();
      setShowWebcamNotDetectedModal(false);
      webcamErrorForceSubmitRef.current = false;
    }
    // Stream is active (we are capturing) - reset consecutive webcam-error counter.
    webcamErrorConsecutiveRef.current = { count: 0 };
    const mimeType = MIME_TYPE_WEBP;
    submitWebcamSnapshot({
      webcamSnapshotDataURI,
      eventGuid: cachedProctorNextImageSequence || 1,
      mimeType,
      nextImageSequence: cachedProctorNextImageSequence || 1,
    });
  };

  useEffect(() => {
    initiateSignedUrlLogging('initiateIntervalFn');
    let webCamSnapshotInterval;
    if (
      !test.is_expired &&
      token &&
      !webCamSnapshotInterval &&
      (videoProctoringEnabled || imageProctoringEnabled) &&
      !test.isTestSubmitted &&
      assessmentId &&
      !isExtendTestInitialPhase
    ) {
      const cachedProctorNextImageSequence = sessionStorage.getItem(
        PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
      );
      if (cachedProctorNextImageSequence < 2) {
        const nextImageSequenceFromSetting = localStorage.getItem(
          IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY
        );
        if (nextImageSequenceFromSetting) {
          sessionStorage.setItem(
            PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY,
            nextImageSequenceFromSetting
          );
        }
        const startTime = new Date();
        setProctorTestStartTimeInCache(startTime.toISOString());
        // Check webcam availability more robustly (stream active AND has live video tracks)
        const stream = webcamRef.current?.stream;
        const hasActiveVideoTrack =
          stream?.active &&
          stream?.getVideoTracks?.()?.length > 0 &&
          stream
            .getVideoTracks()
            .some(track => track.readyState === 'live' && track.enabled);

        if (!hasActiveVideoTrack) {
          proctoringWebcamErrorCallback();
        } else {
          imageCapturingProctoringCallback();
        }
      }
      // Check webcam availability more robustly (stream active AND has live video tracks)
      const checkWebcamAvailable = () => {
        const stream = webcamRef.current?.stream;
        if (!stream?.active) return false;
        const videoTracks = stream?.getVideoTracks?.();
        return (
          videoTracks?.length > 0 &&
          videoTracks.some(
            track => track.readyState === 'live' && track.enabled
          )
        );
      };
      if (!isWebcamCapturingStarted) {
        setWebcamCapturing(true);
      }

      webCamSnapshotInterval = setInterval(
        checkWebcamAvailable()
          ? imageCapturingProctoringCallback
          : proctoringWebcamErrorCallback,
        webcamSnapShotCaptureInterval
      );
    } else if (token) {
      proctoringUBALogger(
        'info:image-not-initiated',
        `webcamEnabled: ${videoProctoringEnabled ||
          imageProctoringEnabled}, isOffline: ${!isOffline},webcam stream: ${
          webcamRef.current?.stream?.active
        }, isTestExpired: ${!test.is_expired}, isSubmitted: ${!test.isTestSubmitted} `
      );
    }
    return () => {
      clearInterval(webCamSnapshotInterval);
    };
  }, [
    assessmentId,
    token,
    test.is_expired,
    webcamSnapShotCaptureInterval,
    imageProctoringEnabled,
    videoProctoringEnabled,
    webcamRef.current?.stream?.active,
    test.isTestSubmitted,
    isSecureBrowserEnable,
    micPermissions,
    submitWebcamSnapshot,
    isExtendTestInitialPhase,
    isWebcamCapturingStarted,
  ]);

  // Session-wide webcam-error auto-submit timer (threshold applies across incidents).
  useEffect(() => {
    let interval;
    if (
      isWebcamErrorAutoSubmitEnabled &&
      token &&
      isWebcamCapturingStarted &&
      !test?.is_expired &&
      !test?.isTestSubmitted &&
      !interval
    ) {
      if (!isStrictCameraEnforcementActive) {
        // In extend initial phase / before session ACTIVE: do not run strict-camera timer at all.
        // Also clear any running incident start-time so time doesn't accumulate while enforcement is inactive.
        clearWebcamErrorBlockingUi();
        clearWebcamErrorStartTime();
        strictCameraEnforcementActivatedAtRef.current = null;
        return undefined;
      }

      // Track when enforcement becomes active (for grace period handling, especially in Firefox)
      if (!strictCameraEnforcementActivatedAtRef.current) {
        strictCameraEnforcementActivatedAtRef.current = Date.now();
      }

      // Compute remaining seconds immediately (not wait for first interval tick)
      const computeRemainingSeconds = () => {
        const startMs = getWebcamErrorStartTimeMs();
        const baseDurationSec = getWebcamErrorDurationSeconds();
        let cachedProctorNextImageSequence = sessionStorage.getItem(
          PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
        );
        cachedProctorNextImageSequence = cachedProctorNextImageSequence || 1;
        if (cachedProctorNextImageSequence === 1) {
          clearWebcamErrorStartTimeDuration();
        }
        // Resume logic: if backend duration already exhausted the threshold, show popup and submit.
        if (
          typeof webcamErrorAutoSubmitTimerSeconds === 'number' &&
          baseDurationSec >= webcamErrorAutoSubmitTimerSeconds &&
          !webcamErrorForceSubmitRef.current
        ) {
          setShowWebcamNotDetectedModal(true);
          setWebcamErrorRemainingSeconds(0);
          webcamErrorForceSubmitRef.current = true;
          dispatch(setAutoSubmitReason(AUTO_SUBMIT_REASONS.WEBCAM_ERROR));
          dispatch(submitTest(true));
          return;
        }

        if (!startMs) {
          const stream = webcamRef.current?.stream;
          const videoTracks = stream?.getVideoTracks?.();
          const hasActiveVideoTrack =
            stream?.active &&
            videoTracks?.length > 0 &&
            videoTracks.some(
              track => track.readyState === 'live' && track.enabled
            );

          // Firefox-specific: Add grace period when enforcement becomes active to allow tracks to become ready
          // This handles the case where webcam access is granted after page load in extend test
          const isFirefox = isFirefoxBrowser();
          const activationTime = strictCameraEnforcementActivatedAtRef.current;
          const timeSinceActivation = activationTime
            ? Date.now() - activationTime
            : Infinity;
          // Grace period: 2 seconds for Firefox, 1 second for other browsers
          const gracePeriodMs = isFirefox ? 2000 : 1000;
          const isWithinGracePeriod = timeSinceActivation < gracePeriodMs;

          // If stream exists and is active but tracks aren't ready yet, and we're within grace period,
          // don't start the error clock yet (tracks might still be initializing)
          if (
            stream?.active &&
            videoTracks?.length > 0 &&
            !hasActiveVideoTrack &&
            isWithinGracePeriod
          ) {
            // Wait for tracks to become ready - don't start error clock yet
            setWebcamErrorRemainingSeconds(null);
            return;
          }

          // Resume + still-unavailable camera: start the incident clock and emit WEBCAM_ERROR_START.
          if (!hasActiveVideoTrack) {
            showWebcamErrorBlockingModalIfEligible();
          } else {
            setWebcamErrorRemainingSeconds(null);
            // Reset activation time when webcam becomes active
            strictCameraEnforcementActivatedAtRef.current = null;
          }
          return;
        }
        const currentElapsedSec = Math.max(0, (getNowMs() - startMs) / 1000);
        const totalSec = baseDurationSec + currentElapsedSec;
        if (typeof webcamErrorAutoSubmitTimerSeconds === 'number') {
          setWebcamErrorRemainingSeconds(
            Math.max(0, webcamErrorAutoSubmitTimerSeconds - totalSec)
          );
        }
        if (
          webcamErrorAutoSubmitTimerSeconds !== null &&
          totalSec >= webcamErrorAutoSubmitTimerSeconds &&
          !webcamErrorForceSubmitRef.current
        ) {
          webcamErrorForceSubmitRef.current = true;
          // Accumulate + emit duration before submitting
          stopWebcamErrorClockIfEnabled();
          dispatch(setAutoSubmitReason(AUTO_SUBMIT_REASONS.WEBCAM_ERROR));
          dispatch(submitTest(true));
        }
      };

      // Compute immediately on mount/resume (don't wait for first interval tick)
      computeRemainingSeconds();

      // Then continue updating every second
      interval = setInterval(computeRemainingSeconds, 1000);
    }
    return () => clearInterval(interval);
  }, [
    clearWebcamErrorBlockingUi,
    dispatch,
    getNowMs,
    isWebcamErrorAutoSubmitEnabled,
    isStrictCameraEnforcementActive,
    showWebcamErrorBlockingModalIfEligible,
    stopWebcamErrorClockIfEnabled,
    test.isTestSubmitted,
    test.is_expired,
    webcamErrorAutoSubmitTimerSeconds,
    token,
    isWebcamCapturingStarted,
  ]);

  useEffect(() => {
    let heartBeatInterval;
    if (
      !test.is_expired &&
      token &&
      !heartBeatInterval &&
      (videoProctoringEnabled || imageProctoringEnabled) &&
      !test.isTestSubmitted &&
      !isExtendTestInitialPhase
    ) {
      heartBeatInterval = setInterval(
        () => {
          let imageSequenceData = localStorage.getItem(
            PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY
          );
          imageSequenceData = imageSequenceData
            ? JSON.parse(imageSequenceData)
            : {};
          const imageSequenceDataKeys = Object.keys(imageSequenceData);
          const requestTransactionId = v4();
          const {
            loggerPostCall: logProctorHeartBeat,
            payload,
          } = proctorHeartBeatLogger({
            token,
            assessmentId: solutionId,
            imageData: imageSequenceData,
            proctorSessionId,
            requestTransactionId,
          });
          logProctorHeartBeat
            .then(() => {
              if (!isSystemOnline) setIsSystemOnline(true);
              // Reset interval to 5 seconds on success
              if (isProctorBatchABEnabled)
                setHeartBeatIntervalTime(PROCTOR_HEARTBEAT_INTERVAL_SUCCESS);
              let currentImageSequenceData = localStorage.getItem(
                PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY
              );
              currentImageSequenceData = currentImageSequenceData
                ? JSON.parse(currentImageSequenceData)
                : {};

              imageSequenceDataKeys.forEach(key => {
                if (currentImageSequenceData[key]) {
                  delete currentImageSequenceData[key];
                }
              });
              localStorage.setItem(
                PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
                JSON.stringify(currentImageSequenceData)
              );

              const lastOfflineStartTime = localStorage.getItem(
                OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY
              );

              if (lastOfflineStartTime) {
                localStorage.setItem(
                  OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY,
                  JSON.stringify([new Date(lastOfflineStartTime), new Date()])
                );
                const duration = (
                  (new Date() - new Date(lastOfflineStartTime)) /
                  1000
                ).toFixed(3);
                localStorage.setItem(
                  OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
                  ''
                );
                let offlineDurationInfo = localStorage.getItem(
                  OFFLINE_DURATION_LOCALSTORAGE_KEY
                );
                offlineDurationInfo =
                  offlineDurationInfo > 0
                    ? +offlineDurationInfo + +duration
                    : +duration;
                localStorage.setItem(
                  OFFLINE_DURATION_LOCALSTORAGE_KEY,
                  offlineDurationInfo
                );
              }
            })
            .catch(error => {
              errorLogger(error, payload);
              proctoringUBALogger(
                'error:heartbeat',
                `error-status: ${error?.response?.status ||
                  error?.name}, request-transaction-id: ${requestTransactionId}`
              );
              if (error.message === NETWORK_ERROR) {
                if (isSystemOnline) setIsSystemOnline(false);
                // Set interval to 1 second on failure
                if (isProctorBatchABEnabled)
                  setHeartBeatIntervalTime(PROCTOR_HEARTBEAT_INTERVAL_FAILURE);
                const lastOfflineStartTime = localStorage.getItem(
                  OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY
                );
                if (!lastOfflineStartTime) {
                  localStorage.setItem(
                    OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
                    new Date()
                  );
                }
              }
            });
        },
        isProctorBatchABEnabled ? heartBeatIntervalTime : 1000
      );
    }
    return () => {
      clearInterval(heartBeatInterval);
    };
  }, [
    token,
    test.is_expired,
    imageProctoringEnabled,
    videoProctoringEnabled,
    test.isTestSubmitted,
    isSystemOnline,
    isExtendTestInitialPhase,
    heartBeatIntervalTime,
    isProctorBatchABEnabled,
  ]);

  useEffect(() => {
    if (webcamRef.current && webcamRef.current.video) {
      webcamRef.current.video.width = 200;
      webcamRef.current.video.height = 150;
    }
  }, [showWebcamPreview, webcamRef.current]);

  /**
   * Send initial FingerPrint
   */
  useEffect(() => {
    if (
      socketActive &&
      !fingerprint &&
      proctorEnabled &&
      solutionId &&
      !test.is_expired &&
      !test.isTestSubmitted
    ) {
      const exclusionCriteria =
        isSecureBrowserEnable && getSystemDetails().os === 'macOS'
          ? {}
          : {
              excludes: {
                adBlock: true,
                fonts: true,
                fontsFlash: true,
                webgl: true,
              },
            };
      Fingerprint2.get(exclusionCriteria, components => {
        const neededForHash = [
          'userAgent',
          'language',
          'timezoneOffset',
          'colorDepth',
          'platform',
          'webglVendorAndRenderer',
          'pixelRatio',
          'screenResolution',
          'availableScreenResolution',
        ];
        const browser = detectBrowser();
        const prepareHash = !_isArray(components)
          ? []
          : components.filter(component =>
              _includes(neededForHash, component.key)
            );
        const platform = prepareHash;
        const values = _map(prepareHash, component => component.value);
        // Secure browser only: fold in the machine identifier (SMBIOS/hardware UUID) so the
        // fingerprint stays stable across tab close/reopen instead of relying on browser-only
        // signals that collide across machines.
        const machineUUID = isSecureBrowserEnable
          ? getSecureBrowserMachineUUID()
          : null;
        if (machineUUID) {
          values.push(machineUUID);
        }
        const fpHash = Fingerprint2.x64hash128(values.join(''), 31);
        const webcamSupport = window.Modernizr
          ? window.Modernizr.video.valueOf()
          : 'Modernizr Error';
        platform.push(
          {
            key: 'browser',
            value: browser,
          },
          {
            key: 'webcam',
            value: webcamSupport,
          }
        );
        setFingerPrint({
          solutionSetId: solutionId,
          token,
          userInfo: {
            username: auth.user.username,
            userEmail: auth.user.email,
            userType: auth.initUserDetails.usertype,
            isStaff: auth.user.is_staff,
            environment: env,
            companySlug: test?.company?.slug,
            activatedModules: auth.modules ? auth.modules : undefined,
          },
          platform,
          trigger: {
            type: 'init',
            fingerprint: {
              id: fpHash,
              components: {
                browser, // for backward compatibility with central
                // raw (unhashed) so backend can flag reuse across sessions/candidates
                // e.g. from a cloned VM image, not just detect it colliding into the same hash
                machine_uuid: machineUUID || undefined,
              },
            },
          },
        });
      });
    }
  }, [
    socketActive,
    fingerprint,
    proctorEnabled,
    solutionId,
    token,
    setFingerPrint,
    auth,
    test.is_expired,
    test.isTestSubmitted,
    isSecureBrowserEnable,
  ]);

  // Shows warning prompt in case user disables webcam in between test.
  // Before starting the test opens an alert if webcam permission not given
  const handleUserMediaError = event => {
    if (event) {
      proctoringUBALogger(
        'error',
        `webcam disabled. error: ${event.name || event}`
      );
      if (isSecureBrowserEnable && window.electron) {
        const { sendMsgToElectron } = window.electron;
        sendMsgToElectron(SECURE_BROWSER_CONST.EVENTS_TO_ELECTRON.SEND_LOGS, {
          message: `webcam disabled. error: ${event.name || event}`,
        });
      }
      dispatch(setWebcamException(event.name || event));
      dispatch(
        emitWebcamEvent({
          eventName: SOCKET_EVENTS.WEBCAM_DISABLED,
          eventGuid: v4(),
          eventData: {
            webcamError: event.name || event,
          },
          eventCreatedAt: getServerTime().toISOString(),
        })
      );
    }

    if (webcamDomException[event?.name]) {
      dispatch(setWebcamException(event.name || event));
      // keep existing behavior for permission-related failures
      dispatch(denyWebcamPermission());
    }
  };

  useEffect(() => {
    const isFeedbackPage = window.location.pathname.includes('/feedback');
    if (isFeedbackPage || (!micPermissions && !audioProctoringEnabled)) return;
    let cleanupFunction = null;
    let isActive = true;
    const init = async () => {
      audioPermissionLoading(true);
      webcamPermissionLoading(true);
      const { cleanup } = await requestAudioVideoPermission({
        constraints: { ...getMicConstraints(), ...getWebcamConstraints() },
        onException: exceptionMessage => {
          audioPermissionLoading(false);
          webcamPermissionLoading(false);
          dispatch(setWebcamException(exceptionMessage));
        },
        onPermissionAllowed: () => {
          audioPermissionLoading(false);
          webcamPermissionLoading(false);
          allowAudioPermission();
          allowWebcamPermission();
        },
        onPermissionDenied: () => {
          audioPermissionLoading(false);
          webcamPermissionLoading(false);
          denyAudioPermission();
          dispatch(denyWebcamPermission());
        },
        onVideoTrackEnded: showWebcamErrorBlockingModalIfEligible,
      });
      if (!isActive) {
        cleanup();
      }
      cleanupFunction = cleanup;
    };

    init();
    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
      cleanupFunction?.();
    };
  }, [micPermissions, audioProctoringEnabled, webcamRenderKey]);

  const handleVoiceDetected = useCallback(
    voiceData => {
      // Handle different types of voice detection events
      if (voiceData.type === 'microphone_muted') {
        proctoringUBALogger(
          'warning',
          `Microphone muted: ${voiceData.reason}. Mute count: ${voiceData.muteCount}`
        );
        // Store mute data and show microphone not detected modal when muted
        setMicrophoneMuteData(voiceData);
        setShowMicrophoneNotDetectedModal(true);
      } else if (voiceData.type === 'microphone_unmuted') {
        proctoringUBALogger(
          'info',
          `Microphone unmuted after ${voiceData.muteDurationSeconds}s. Total mute count: ${voiceData.muteCount}`
        );
        // Update mute data with duration and hide microphone modal when unmuted
        setMicrophoneMuteData(voiceData);
        setShowMicrophoneNotDetectedModal(false);
      } else if (voiceData.type === 'voice_detected') {
        proctoringUBALogger(
          'info',
          `Voice detected for ${voiceData.duration}ms with incident count ${voiceData.incidentCount}`
        );

        // Log mute information if available
        if (voiceData.microphoneMuteState?.currentMuteDuration > 0) {
          proctoringUBALogger(
            'info',
            `Current mute duration: ${voiceData.microphoneMuteState.currentMuteDurationSeconds}s`
          );
        }
      }
    },
    [proctoringUBALogger]
  );

  const handleMicrophoneError = useCallback(
    errorMessage => {
      proctoringUBALogger('error', `Microphone error: ${errorMessage}`);
      setShowMicrophoneNotDetectedModal(true);
    },
    [proctoringUBALogger]
  );

  const handleUserMedia = stream => {
    if (stream && !test.is_expired) {
      dispatch(setWebcamException(null));
      const videoTracks = stream?.getVideoTracks?.() || [];
      const hasActiveVideoTrack =
        stream?.active &&
        videoTracks.length > 0 &&
        videoTracks.some(track => track.readyState === 'live' && track.enabled);

      // eslint-disable-next-line no-param-reassign
      stream.oninactive = async () => {
        logSecureBrowser({ message: 'webcam became inactive' });
        proctoringUBALogger('info', `webcam became inactive`);
        const ctx = proctorCaptureContextRef.current;
        if (!ctx.isExtendTestInitialPhase && ctx.isWebcamCapturingStarted) {
          showWebcamErrorBlockingModalIfEligible();
          scheduleWebcamRecovery({
            reason: 'stream-oninactive',
          });
        }
      };

      // Listen for track ended events (camera revoked in OS/browser settings)
      videoTracks.forEach(videoTrack => {
        videoTrack.addEventListener('ended', () => {
          console.warn(
            '[Camera] Track ended – possibly revoked in OS settings'
          );
          dispatch(setWebcamException(webcamDomException.NotAllowedError));
          const ctx = proctorCaptureContextRef.current;
          if (!ctx.isExtendTestInitialPhase && ctx.isWebcamCapturingStarted) {
            showWebcamErrorBlockingModalIfEligible();
          }
        });
      });

      // Guard: `onUserMedia` can fire with a stream, but tracks may not actually be live.
      // Only treat this as "recovered" when we have a live enabled video track.
      const ctx = proctorCaptureContextRef.current;
      if (
        !ctx.isExtendTestInitialPhase &&
        ctx.isWebcamCapturingStarted &&
        !hasActiveVideoTrack
      ) {
        showWebcamErrorBlockingModalIfEligible();
        return;
      }

      proctoringUBALogger('info', `webcam enabled`);
      stopWebcamErrorClockIfEnabled();
      if (isWebcamErrorAutoSubmitEnabled) {
        setShowWebcamNotDetectedModal(false);
      }
      // Webcam is active again - reset consecutive webcam-error counter.
      webcamErrorConsecutiveRef.current = { count: 0 };
      // Reset activation time when webcam becomes active (handles Firefox extend test case)
      strictCameraEnforcementActivatedAtRef.current = null;
      // If a recovery attempt was in progress, mark it successful and log it.
      if (webcamRecoveryRef.current.inProgress) {
        const meta = {
          attempt: webcamRecoveryRef.current.attempt,
          reason: webcamRecoveryRef.current.reason,
          startedAt: webcamRecoveryRef.current.startedAt,
        };
        proctoringUBALogger(
          'info:webcam-recovery',
          `webcam recovery succeeded (attempt=${meta.attempt}, reason=${meta.reason})`
        );
        logSecureBrowser({
          message: 'webcam recovery succeeded',
          ...meta,
        });
        webcamRecoveryRef.current.inProgress = false;
        webcamRecoveryRef.current.reason = null;
        webcamRecoveryRef.current.lastErrorName = null;
        webcamRecoveryRef.current.startedAt = null;
        if (typeof webcamRecoveryAttemptResolveRef.current === 'function') {
          webcamRecoveryAttemptResolveRef.current(true);
        }
        webcamRecoveryAttemptResolveRef.current = null;
      }
      dispatch(
        emitWebcamEvent({
          eventName: SOCKET_EVENTS.WEBCAM_ENABLED,
          eventData: {},
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
        })
      );
      allowWebcamPermission();
    }
  };

  /**
   * These functions are shared among 2 components
   */
  const clearClipboard = () => {
    emptyClipboard(preventCopyRef.current);
  };
  const setFullScreen = () => {
    // When candidate resume the test . Due to closing tab and reopening will trigger BT . In secure browser fullscreen handle by ElectronJs. If we  handle from this behaviour from browser ,  ESC button close fullscreen mode of electron application too. PROC-628
    const isIOSDevice = isIOSWebView();
    if (isSecureBrowserEnable || isIOSDevice) {
      return;
    }

    // Find the right method
    const element = document.documentElement;
    element.requestFullscreen =
      element.requestFullscreen ||
      element.mozRequestFullScreen ||
      element.webkitRequestFullscreen ||
      element.msRequestFullscreen;
    if (element.requestFullscreen) {
      const fullscreenResult = element.requestFullscreen();
      if (fullscreenResult) {
        fullscreenResult
          .then(() => {
            proctoringUBALogger(
              'Proctor:FullScreen',
              'requestFullscreen() promise resolved'
            );
          })
          .catch(error => {
            proctoringUBALogger(
              'Proctor:FullScreen',
              JSON.stringify({
                event: 'requestFullscreen-rejected',
                error: error?.message || error?.name,
              })
            );
          });
      }
    } else {
      proctoringUBALogger(
        'Proctor:FullScreen',
        'requestFullscreen API not available on document.documentElement'
      );
    }
  };

  useEffect(() => {
    proctorBatchSignedUrlsRef.current = proctoringSignedUrls;
  }, [proctoringSignedUrls]);

  useEffect(() => {
    // Emitter functionality
    const channel = new BroadcastChannel(TAB_COMMUNICATION_CHANNEL);
    if (!isOffline) {
      const emitPayload = () => {
        const payload = {
          timeStamp: window.currentEmitTime,
          tabId,
        };
        channel.postMessage(JSON.stringify(payload));
      };

      // Listener functionality
      const handleIncomingMessage = event => {
        const eventData = JSON.parse(event.data);
        if (
          eventData.timeStamp !== JSON.stringify(window.currentEmitTime) ||
          tabId !== eventData.tabId
        ) {
          proctoringUBALogger(
            `new tab opens , session start time ${window.currentEmitTime} tabId ${tabId}  multitab `,
            event.data
          );
          history.replace('/multi-session');
        }
      };

      // Attach listeners
      channel.onmessage = handleIncomingMessage;

      if (tabId && !disableMultiTab) {
        emitPayload();
      }
    }

    // Clean up listeners
    return () => {
      channel.close();
    };
  }, [tabId, isOffline, disableMultiTab]);

  return (
    <>
      {showWebcamNotDetectedModal && (
        <>
          {isWebcamErrorAutoSubmitEnabled ? (
            <WebcamErrorBlockingModal
              isOpen={showWebcamNotDetectedModal}
              remainingSeconds={
                typeof webcamErrorRemainingSeconds === 'number'
                  ? webcamErrorRemainingSeconds
                  : webcamErrorAutoSubmitTimerSeconds
              }
              isSecureBrowserEnable={Boolean(isSecureBrowserEnable)}
              onRefresh={() => window.location.reload()}
              onExit={() => exitSecureBrowserApplication()}
            />
          ) : (
            <WebcamNotDetectedModal
              isOpen={showWebcamNotDetectedModal}
              verbose={
                audioProctoringEnabled ? 'webcam or microphone' : 'webcam'
              }
              title={`${
                audioProctoringEnabled ? 'Webcam or Microphone' : 'Webcam'
              } Not Detected`}
            />
          )}
        </>
      )}
      {audioProctoringEnabled && showMicrophoneNotDetectedModal && (
        <MicrophoneNotDetectedModal
          isOpen={showMicrophoneNotDetectedModal}
          muteData={microphoneMuteData}
        />
      )}
      {webcamEnabled && (
        <WebcamDraggable
          ref={webcamDraggableRef}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            position: 'fixed',
            top: 0,
            left: 0,
            touchAction: 'none',
            userSelect: 'none',
            visibility: isAiInterview ? 'hidden' : 'visible',
          }}
          onMouseDown={dragHandlers.onMouseDown}
          onPointerDown={dragHandlers.onPointerDown}
          onTouchStart={dragHandlers.onTouchStart}
          className={classNames({
            'minimize-webcam-preview': !showWebcamPreview,
          })}
        >
          <WebcamPreviewControllers>
            <div
              className={classNames('d-flex', {
                'justify-content-between': !showWebcamPreview,
                'justify-content-end': showWebcamPreview,
              })}
            >
              {!showWebcamPreview && webcamPermission && (
                <div>
                  <BlinkingDot>
                    <i className="fa fa-circle" />
                  </BlinkingDot>
                  <span style={{ fontSize: '14px' }}>Webcam Recording</span>
                </div>
              )}

              {!showWebcamPreview && !webcamPermission && (
                <div>
                  <BlinkingDot>
                    <i className="fa fa-circle" />
                  </BlinkingDot>
                  <span style={{ fontSize: '14px' }}>Webcam Not Detected</span>
                </div>
              )}

              <span>
                {showWebcamPreview ? (
                  <i
                    className="fa fa-compress webcam-preview-icon cursor-pointer"
                    onClick={handleMinimizeWebcam}
                  />
                ) : (
                  <i
                    className="fa fa-expand webcam-preview-icon cursor-pointer"
                    onClick={handleMaximizeWebcam}
                  />
                )}
              </span>
            </div>
            {!webcamPermission && (
              <div
                className="d-flex align-items-center"
                style={{ height: '75px' }}
              >
                <WebcamNotDetectedIcon
                  className="img-responsive center-block d-block mx-auto cursor-pointer"
                  src={IMAGES.WEBCAM_NOT_FOUND}
                />
              </div>
            )}
          </WebcamPreviewControllers>
          <Webcam
            key={webcamRenderKey}
            audio={Boolean(micPermissions)}
            ref={webcamRef}
            screenshotFormat={
              isSafariBrowser() ? MIME_TYPE_JPEG : MIME_TYPE_WEBP
            }
            videoConstraints={buildVideoConstraints(videoConstraints)}
            minScreenshotHeight={480}
            minScreenshotWidth={640}
            screenshotQuality={0.9}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserMediaError}
            id="ds-webcam"
          />
        </WebcamDraggable>
      )}
      {!isSecureBrowserEnable && (
        <FullScreenProctorContainer
          clearClipboard={clearClipboard}
          setFullScreen={setFullScreen}
        />
      )}
      {!isExtendTestInitialPhase && (
        <VisibilityProctorContainer
          isBrowserToleranceEnabled={isBrowserToleranceEnabled}
        />
      )}
      <PreventCopyPastInput ref={preventCopyRef} type="text" value="" />
      {isBrowserToleranceEnabled && !isExtendTestInitialPhase && (
        <BrowserToleranceContainer
          setFullScreen={setFullScreen}
          clearClipboard={clearClipboard}
        />
      )}
      {proctorEnabled && assessmentId && <PreventCopyPast />}
      {enableRouteChangeProctor && (
        <RouteChangeTracking
          location={location}
          trackChangeRoute={trackChangeRoute}
        />
      )}
      {webcamEnabled &&
        videoProctoringEnabled &&
        !isWebcamErrorAutoSubmitEnabled && (
          <RealTimeProctoringFeedback
            webcamRef={webcamRef}
            isFeedbackEnabled={isRealtimeFeedbackEnabled}
            externalAlertSignal={realtimeFeedbackModalSignal}
          />
        )}

      {/* Voice Activity Detection for Audio Proctoring */}
      {audioProctoringEnabled && (
        <VAD
          isEnabled={audioProctoringEnabled}
          onVoiceDetected={handleVoiceDetected}
          onMicrophoneError={handleMicrophoneError}
          test={test}
          assessmentId={assessmentId}
          token={token}
          isExtendTestInitialPhase={isExtendTestInitialPhase}
          offsetTime={offsetTime}
        />
      )}
    </>
  );
}

export default withRouter(Proctor);
